import { useRef, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, ContactShadows, Sparkles } from "@react-three/drei";
import * as THREE from "three";

/* ---------- Canvas texture helpers (no external assets) ---------- */

const FONT = '"Space Mono", monospace';
const NAVY = "#172554";

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function star(ctx, x, y, r, color) {
  ctx.save();
  ctx.translate(x, y);
  ctx.beginPath();
  for (let i = 0; i < 5; i++) {
    const a = -Math.PI / 2 + i * ((2 * Math.PI) / 5);
    const a2 = a + Math.PI / 5;
    ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
    ctx.lineTo(Math.cos(a2) * r * 0.45, Math.sin(a2) * r * 0.45);
  }
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
  ctx.restore();
}

/* Large gear face — US/CPA clock dial with stars */
function makeClockFace() {
  const c = document.createElement("canvas");
  c.width = 512;
  c.height = 512;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, 512, 512);

  ctx.beginPath();
  ctx.arc(256, 256, 240, 0, Math.PI * 2);
  ctx.lineWidth = 36;
  ctx.strokeStyle = NAVY;
  ctx.stroke();

  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    const quarter = i % 3 === 0;
    ctx.beginPath();
    ctx.moveTo(256 + Math.cos(a) * 200, 256 + Math.sin(a) * 200);
    ctx.lineTo(256 + Math.cos(a) * (quarter ? 230 : 224), 256 + Math.sin(a) * (quarter ? 230 : 224));
    ctx.lineWidth = quarter ? 10 : 6;
    ctx.strokeStyle = "#ffffff";
    ctx.stroke();
    const sa = a + Math.PI / 12;
    star(ctx, 256 + Math.cos(sa) * 240, 256 + Math.sin(sa) * 240, 9, "rgba(255,255,255,0.9)");
  }

  [130, 168, 196].forEach((r, i) => {
    ctx.beginPath();
    ctx.arc(256, 256, r, 0, Math.PI * 2);
    ctx.lineWidth = 2;
    ctx.strokeStyle = `rgba(11,18,32,${0.16 - i * 0.04})`;
    ctx.stroke();
  });

  ctx.save();
  ctx.shadowColor = "rgba(255,107,0,0.85)";
  ctx.shadowBlur = 26;
  ctx.beginPath();
  ctx.arc(256, 256, 152, -0.5, 0.9);
  ctx.lineWidth = 20;
  ctx.lineCap = "round";
  ctx.strokeStyle = "#FF6B00";
  ctx.stroke();
  ctx.restore();

  ctx.beginPath();
  ctx.arc(256, 256, 152, 2.3, 2.85);
  ctx.lineWidth = 12;
  ctx.lineCap = "round";
  ctx.strokeStyle = "#0E9384";
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(256, 256, 48, 0, Math.PI * 2);
  ctx.fillStyle = NAVY;
  ctx.fill();
  star(ctx, 256, 258, 24, "#ffffff");

  ctx.fillStyle = "rgba(11,18,32,0.55)";
  ctx.font = `bold 22px ${FONT}`;
  ctx.textAlign = "center";
  ctx.fillText("EST", 256, 340);
  ctx.fillText("IST", 256, 186);

  return new THREE.CanvasTexture(c);
}

/* Small gear face — India / Ashoka Chakra spokes */
function makeChakraFace() {
  const c = document.createElement("canvas");
  c.width = 384;
  c.height = 384;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, 384, 384);

  ctx.beginPath();
  ctx.arc(192, 192, 174, -Math.PI, 0);
  ctx.lineWidth = 26;
  ctx.strokeStyle = "#F59331";
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(192, 192, 174, 0, Math.PI);
  ctx.lineWidth = 26;
  ctx.strokeStyle = "#12880F";
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(192, 192, 174, 0, Math.PI * 2);
  ctx.lineWidth = 5;
  ctx.strokeStyle = NAVY;
  ctx.stroke();

  for (let i = 0; i < 24; i++) {
    const a = (i / 24) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(192 + Math.cos(a) * 34, 192 + Math.sin(a) * 34);
    ctx.lineTo(192 + Math.cos(a) * 158, 192 + Math.sin(a) * 158);
    ctx.lineWidth = 3;
    ctx.strokeStyle = NAVY;
    ctx.stroke();
  }

  ctx.beginPath();
  ctx.arc(192, 192, 36, 0, Math.PI * 2);
  ctx.fillStyle = NAVY;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(192, 192, 12, 0, Math.PI * 2);
  ctx.fillStyle = "#ffffff";
  ctx.fill();

  return new THREE.CanvasTexture(c);
}

/* Floating deadline chip */
function makeChipTexture() {
  const c = document.createElement("canvas");
  c.width = 448;
  c.height = 160;
  const ctx = c.getContext("2d");
  roundRect(ctx, 0, 0, 448, 160, 26);
  ctx.fillStyle = "#0B1220";
  ctx.fill();
  ctx.lineWidth = 4;
  ctx.strokeStyle = "rgba(255,107,0,0.65)";
  ctx.stroke();

  ctx.fillStyle = "#94a3b8";
  ctx.font = `700 20px ${FONT}`;
  ctx.fillText("TAX DEADLINE", 30, 52);

  ctx.save();
  ctx.shadowColor = "rgba(255,107,0,0.9)";
  ctx.shadowBlur = 18;
  ctx.fillStyle = "#FF8A3D";
  ctx.font = `bold 46px ${FONT}`;
  ctx.fillText("APR 15", 30, 116);
  ctx.restore();

  ctx.fillStyle = "#e2e8f0";
  ctx.font = `bold 24px ${FONT}`;
  ctx.fillText("11:59 PM", 170, 114);

  ctx.beginPath();
  ctx.arc(372, 80, 44, 0, Math.PI * 2);
  ctx.lineWidth = 10;
  ctx.strokeStyle = "rgba(255,255,255,0.12)";
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(372, 80, 44, -Math.PI / 2, Math.PI);
  ctx.lineWidth = 10;
  ctx.lineCap = "round";
  ctx.strokeStyle = "#34D399";
  ctx.stroke();

  return new THREE.CanvasTexture(c);
}

/* Tax form card + coin (business context floaters) */
function makeFormTexture(formNo) {
  const c = document.createElement("canvas");
  c.width = 256;
  c.height = 336;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, 256, 336);
  ctx.strokeStyle = "rgba(11,18,32,0.2)";
  ctx.lineWidth = 6;
  roundRect(ctx, 6, 6, 244, 324, 16);
  ctx.stroke();
  ctx.fillStyle = "rgba(11,18,32,0.45)";
  ctx.font = `700 18px ${FONT}`;
  ctx.fillText("FORM", 24, 44);
  ctx.fillStyle = "#FF6B00";
  ctx.font = `bold 62px ${FONT}`;
  ctx.fillText(formNo, 24, 112);
  ctx.fillStyle = "rgba(11,18,32,0.35)";
  ctx.font = `700 15px ${FONT}`;
  ctx.fillText("US TAX RETURN", 24, 140);
  for (let i = 0; i < 6; i++) {
    ctx.strokeStyle = "rgba(11,18,32,0.12)";
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(24, 172 + i * 24);
    ctx.lineTo(232, 172 + i * 24);
    ctx.stroke();
  }
  return new THREE.CanvasTexture(c);
}

function makeCoinTexture() {
  const c = document.createElement("canvas");
  c.width = 256;
  c.height = 256;
  const ctx = c.getContext("2d");
  ctx.clearRect(0, 0, 256, 256);
  ctx.beginPath();
  ctx.arc(128, 128, 120, 0, Math.PI * 2);
  ctx.fillStyle = "#F5A73B";
  ctx.fill();
  ctx.beginPath();
  ctx.arc(128, 128, 96, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(255,255,255,0.85)";
  ctx.lineWidth = 8;
  ctx.stroke();
  ctx.fillStyle = "#ffffff";
  ctx.font = `bold 132px ${FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("$", 128, 134);
  return new THREE.CanvasTexture(c);
}

/* ---------- 3D objects ---------- */

function Gear({ radius, depth, teeth, faceTex, speed }) {
  const g = useRef();
  useFrame((_, d) => {
    if (g.current) g.current.rotation.z += d * speed;
  });
  const toothH = radius * 0.2;
  const toothW = radius * 0.2;
  const angles = useMemo(() => Array.from({ length: teeth }, (_, i) => (i / teeth) * Math.PI * 2), [teeth]);
  return (
    <group ref={g}>
      {angles.map((a, i) => (
        <mesh
          key={i}
          position={[Math.cos(a) * (radius + toothH * 0.3), Math.sin(a) * (radius + toothH * 0.3), 0]}
          rotation={[0, 0, a + Math.PI / 2]}
        >
          <boxGeometry args={[toothW, toothH * 0.9, depth * 0.8]} />
          <meshStandardMaterial color="#e6ecf7" roughness={0.3} metalness={0.3} />
        </mesh>
      ))}
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[radius, radius, depth, 72]} />
        <meshStandardMaterial color="#f2f6fc" roughness={0.3} metalness={0.35} />
      </mesh>
      <mesh position={[0, 0, depth / 2 + 0.004]}>
        <circleGeometry args={[radius * 0.93, 72]} />
        <meshStandardMaterial map={faceTex} roughness={0.42} metalness={0.05} />
      </mesh>
      <mesh position={[0, 0, depth / 2 + 0.03]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[radius * 0.15, radius * 0.15, 0.06, 32]} />
        <meshStandardMaterial color="#0B1220" roughness={0.3} metalness={0.4} />
      </mesh>
    </group>
  );
}

function SweepArc({ radius, speed, z }) {
  const ref = useRef();
  useFrame((_, d) => {
    if (ref.current) ref.current.rotation.z += d * speed;
  });
  return (
    <mesh ref={ref} position={[0, 0, z]}>
      <torusGeometry args={[radius, 0.022, 8, 64, 1.0]} />
      <meshBasicMaterial color="#FF6B00" transparent opacity={0.9} />
    </mesh>
  );
}

function GearClock() {
  const clockTex = useMemo(() => makeClockFace(), []);
  const chakraTex = useMemo(() => makeChakraFace(), []);
  const chipTex = useMemo(() => makeChipTexture(), []);
  return (
    <Float speed={1.1} rotationIntensity={0.12} floatIntensity={0.55}>
      <group rotation={[-0.06, 0.24, 0]}>
        <Gear radius={1.28} depth={0.22} teeth={16} faceTex={clockTex} speed={0.22} />
        <group position={[2.0, 1.0, -0.16]}>
          <Gear radius={0.86} depth={0.2} teeth={12} faceTex={chakraTex} speed={-0.22 * (16 / 12)} />
        </group>
        <SweepArc radius={1.6} speed={0.85} z={0.16} />

        <Float speed={1.6} rotationIntensity={0.15} floatIntensity={0.8}>
          <mesh position={[1.62, -0.62, 0.45]} rotation={[0, 0.38, 0.03]}>
            <boxGeometry args={[1.5, 0.54, 0.04]} />
            <meshStandardMaterial map={chipTex} roughness={0.4} metalness={0.1} />
          </mesh>
        </Float>

        <Float speed={1.5} rotationIntensity={0.3} floatIntensity={1}>
          <mesh position={[-1.6, -1.25, 0.5]} rotation={[0.12, 0.5, 0.1]}>
            <boxGeometry args={[0.58, 0.76, 0.02]} />
            <meshStandardMaterial map={useMemo(() => makeFormTexture("1040"), [])} roughness={0.45} />
          </mesh>
        </Float>

        <Coin position={[-2.35, -0.5, 0.2]} scale={0.55} />
        <Coin position={[2.05, -1.95, 0.3]} scale={0.75} />
      </group>
    </Float>
  );
}

function Coin({ position, scale = 1 }) {
  const tex = useMemo(() => makeCoinTexture(), []);
  const mats = useMemo(
    () => [
      new THREE.MeshStandardMaterial({ color: "#E8930C", metalness: 0.65, roughness: 0.3 }),
      new THREE.MeshStandardMaterial({ map: tex, metalness: 0.35, roughness: 0.35 }),
      new THREE.MeshStandardMaterial({ map: tex, metalness: 0.35, roughness: 0.35 }),
    ],
    [tex]
  );
  return (
    <Float speed={2} rotationIntensity={0.8} floatIntensity={1.4}>
      <mesh position={position} rotation={[Math.PI / 2.3, 0.4, 0]} scale={scale} material={mats}>
        <cylinderGeometry args={[0.3, 0.3, 0.06, 48]} />
      </mesh>
    </Float>
  );
}

function Particles({ count = 900, color, spread, opacity = 0.4 }) {
  const ref = useRef();
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = spread * (0.55 + Math.random() * 0.45);
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      arr[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      arr[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      arr[i * 3 + 2] = r * Math.cos(phi);
    }
    return arr;
  }, [count, spread]);
  useFrame((state, delta) => {
    if (ref.current) ref.current.rotation.y += delta * 0.03;
  });
  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial size={0.035} color={color} transparent opacity={opacity} sizeAttenuation depthWrite={false} blending={THREE.AdditiveBlending} />
    </points>
  );
}

function Rig({ children }) {
  const group = useRef();
  useFrame((state) => {
    if (!group.current) return;
    const { pointer } = state;
    group.current.rotation.y = THREE.MathUtils.lerp(group.current.rotation.y, pointer.x * 0.3, 0.045);
    group.current.rotation.x = THREE.MathUtils.lerp(group.current.rotation.x, -pointer.y * 0.16, 0.045);
  });
  return <group ref={group}>{children}</group>;
}

export default function Hero3D() {
  return (
    <div className="absolute inset-0" data-testid="hero-3d-canvas" aria-hidden="true">
      <Canvas
        camera={{ position: [0, 0.3, 6.8], fov: 44 }}
        dpr={[1, 1.6]}
        gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
      >
        <ambientLight intensity={1.15} />
        <pointLight position={[-6, 3, 4]} intensity={85} color="#2563EB" />
        <pointLight position={[6, -2, 3]} intensity={75} color="#FF6B00" />
        <directionalLight position={[2, 7, 5]} intensity={1.5} color="#ffffff" />
        <Rig>
          <group position={[1.05, 0, 0]} scale={0.97}>
            <GearClock />
          </group>
          <Particles count={600} color="#2563EB" spread={8} opacity={0.35} />
          <Particles count={300} color="#FF6B00" spread={6.5} opacity={0.3} />
        </Rig>
        <ContactShadows position={[0, -2.7, 0]} opacity={0.2} scale={14} blur={2.6} far={5} color="#1e3a8a" />
        <Sparkles count={45} scale={12} size={2} speed={0.22} color="#2563EB" opacity={0.35} />
      </Canvas>
    </div>
  );
}
